import { useCallback, useEffect, useRef, useState } from 'react';
import type { NavigateFunction } from 'react-router-dom';
import type { TFunction } from 'i18next';
import { apiService } from '@/services/apiService';
import { openPaymentPage, shouldOpenInExternalTab } from '@/features/finance/utils/paymentUrlStrategy';
import { DEPOSIT_CANCELLED_EVENT, type DepositCancelledDetail } from '@/utils/depositCancelled';

/**
 * Pending deposits the player can still pay: the pending-payment floater and the Money tab's
 * "Waiting for payment" rows read the same list and resume the same way.
 */
export interface PendingDeposit {
  id: string;
  paymentUrl: string;
  transactionId?: string;
  platformName: string;
  platformSlug: string;
  amount: number;
  status: 'pending';
  createdAt: string;
  provider?: string | null;
  isWalletDeposit: boolean;
}

/** One `/payment/history` row, as far as pending deposits need it. */
export interface PendingDepositHistoryRow {
  id?: number | string;
  transactionId?: string;
  type?: string;
  status?: string;
  paymentUrl?: string | null;
  amount?: number | string;
  createdAt?: string;
  provider?: string | null;
  depositDetails?: Array<{
    gameProvider?: {
      name?: string;
      code?: string;
    };
    amount?: string | number;
    status?: string;
  }>;
}

/** A payment link stays usable for 30 minutes; the list shows at most five. */
export const PENDING_DEPOSIT_WINDOW_MS = 30 * 60 * 1000;
export const PENDING_DEPOSIT_LIMIT = 5;

const normalizePendingStatus = (rawStatus: string): 'pending' | null => (rawStatus === 'pending' ? 'pending' : null);

const normalizeProviderSlug = (slug: string) => slug.toLowerCase().replace(/\s+/g, '-');

const isWalletDepositName = (name: string) => name.trim().toLowerCase() === 'deposit';

export function mapHistoryToPending(rows: PendingDepositHistoryRow[]): PendingDeposit[] {
  const cutoff = Date.now() - PENDING_DEPOSIT_WINDOW_MS;
  const mapped = rows.flatMap((tx): PendingDeposit[] => {
    const rawStatus = String(tx.status || '').toLowerCase();
    const status = normalizePendingStatus(rawStatus);
    const paymentUrl = tx.paymentUrl || '';
    if (!status || tx.type !== 'deposit' || !paymentUrl || !tx.createdAt) return [];

    const createdAtMs = new Date(tx.createdAt).getTime();
    if (Number.isNaN(createdAtMs) || createdAtMs < cutoff) return [];

    const primaryDetail = tx.depositDetails?.[0];
    const hasGameProvider = Boolean(primaryDetail?.gameProvider?.name || primaryDetail?.gameProvider?.code);
    const providerName = primaryDetail?.gameProvider?.name || 'Deposit';
    const providerCode = primaryDetail?.gameProvider?.code || providerName;
    const amountRaw = primaryDetail?.amount ?? tx.amount ?? 0;
    const amount = typeof amountRaw === 'number' ? amountRaw : Number(amountRaw) || 0;

    return [
      {
        id: tx.transactionId || String(tx.id || ''),
        paymentUrl,
        transactionId: tx.transactionId,
        platformName: providerName,
        platformSlug: normalizeProviderSlug(providerCode),
        amount,
        status,
        createdAt: tx.createdAt || new Date().toISOString(),
        provider: tx.provider ?? null,
        isWalletDeposit: !hasGameProvider || isWalletDepositName(providerName),
      },
    ];
  });

  mapped.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  return mapped.slice(0, PENDING_DEPOSIT_LIMIT);
}

/** Pending deposits from the last 30 minutes (forced past the API cache). Throws on network errors. */
export async function fetchPendingDeposits(): Promise<PendingDeposit[]> {
  const now = Date.now();
  const from = new Date(now - PENDING_DEPOSIT_WINDOW_MS).toISOString();
  const to = new Date(now).toISOString();
  const response = await apiService.get<{ transactions?: PendingDepositHistoryRow[] }>(
    `/payment/history?page=1&limit=${PENDING_DEPOSIT_LIMIT}&type=deposit&status=pending&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    true,
    true
  );
  return mapHistoryToPending(response.data?.transactions || []);
}

/**
 * Resume a pending payment: providers that cannot be framed open in a new tab, and the player
 * lands on the page that runs that payment (wallet top-up or the deposit wizard) with its state.
 */
export function resumePendingDeposit(tx: PendingDeposit, navigate: NavigateFunction): void {
  if (shouldOpenInExternalTab(tx.provider)) {
    openPaymentPage(tx.paymentUrl);
  }

  const resumeState = {
    resumePayment: {
      paymentUrl: tx.paymentUrl,
      transactionId: tx.transactionId,
      amount: tx.amount,
      platformName: tx.platformName,
      provider: tx.provider,
      source: 'pending_floater' as const,
    },
  };

  if (tx.isWalletDeposit) {
    navigate('/account/finance/wallet/deposit', { state: resumeState });
    return;
  }

  // Legacy game checkout resume → Deposit (wallet-only game funding).
  navigate('/account/finance/deposit', { state: resumeState });
}

/** "3m ago" for a pending row. */
export function formatPendingTimeAgo(iso: string, t: TFunction): string {
  const value = new Date(iso).getTime();
  if (Number.isNaN(value)) return t('transactions.pendingFloater.justNow');
  const seconds = Math.max(0, Math.floor((Date.now() - value) / 1000));
  if (seconds < 60) return t('transactions.pendingFloater.secondsAgo', { count: seconds });
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return t('transactions.pendingFloater.minutesAgo', { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('transactions.pendingFloater.hoursAgo', { count: hours });
  const days = Math.floor(hours / 24);
  return t('transactions.pendingFloater.daysAgo', { count: days });
}

/**
 * The pending list for a page (the Money tab): loaded on mount when signed in, kept on network
 * errors, and a deposit cancelled anywhere in the app drops out at once.
 */
export function usePendingDeposits(): { pending: PendingDeposit[]; refresh: () => Promise<void> } {
  const [pending, setPending] = useState<PendingDeposit[]>([]);
  const mountedRef = useRef(true);

  const refresh = useCallback(async () => {
    if (!apiService.getAccessToken()) {
      setPending([]);
      return;
    }
    try {
      const next = await fetchPendingDeposits();
      if (mountedRef.current) setPending(next);
    } catch {
      // Keep the previous list on network failures.
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    void refresh();
    return () => {
      mountedRef.current = false;
    };
  }, [refresh]);

  useEffect(() => {
    const onCancelled = (event: Event) => {
      const transactionId = (event as CustomEvent<DepositCancelledDetail>).detail?.transactionId;
      if (!transactionId) return;
      setPending((prev) => prev.filter((tx) => tx.transactionId !== transactionId && tx.id !== transactionId));
    };
    window.addEventListener(DEPOSIT_CANCELLED_EVENT, onCancelled);
    return () => window.removeEventListener(DEPOSIT_CANCELLED_EVENT, onCancelled);
  }, []);

  return { pending, refresh };
}
